import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Skeleton } from 'primereact/skeleton';
import CustomTooltip from '../../../../../shared/components/common/tooltip/CustomTooltip';
import EmptyPanel from '../../../../../shared/components/empty-panel/EmptyPanel';
import StackedHorizontalBarChart, {
  StackedBarData,
} from '../../../../../shared/components/chart/stacked-horizontal-bar-chart/StackedHorizontalBarChart';
import { UrlTypesStatsResponse } from '../../../../../shared/services/monitor/data/sources/MonitorDataSourcesService';
import { getPageTypeChartColor } from '../../../../../shared/components/page-type-tag/PageTypeTag';
import './UrlTypesChart.scss';

interface UrlTypesChartProps {
  urlTypesStats: UrlTypesStatsResponse[] | undefined;
  isLoading: boolean;
}

const UrlTypesChart: React.FC<UrlTypesChartProps> = ({ urlTypesStats, isLoading }) => {
  const { t } = useTranslation();

  const stackedBarData: StackedBarData[] = useMemo(() => {
    if (!urlTypesStats || urlTypesStats.length === 0) return [];

    const total = urlTypesStats.reduce((sum, item) => sum + item.count, 0);
    const sorted = [...urlTypesStats].sort((a, b) => b.count - a.count);

    return sorted.map((item) => ({
      label: t(`pageType_${item.pageType}`),
      value: total > 0 ? Math.max(0.1, Number(((item.count / total) * 100).toFixed(1))) : 0,
      color: getPageTypeChartColor(item.pageType),
    }));
  }, [urlTypesStats, t]);

  const hasData = stackedBarData.length > 0;

  return (
    <div className="stats__panel url-types-chart">
      <div className="page-box__title-action">
        <div className="page-box__title page-box__title--flex">
          {t('urlTypes')}
          <CustomTooltip tooltipText="urlTypesTooltip" addMargin={false} />
        </div>
      </div>
      {isLoading ? (
        <div className="stats-box stats-box--bar-chart">
          <div className="stats-box__content">
            <Skeleton className="skeleton--chart-bar">
              <span />
            </Skeleton>
          </div>
        </div>
      ) : !hasData ? (
        <div className="stats-box stats-box--bar-chart stats-box--empty">
          <div className="stats-box__content stats-box__content--empty">
            <EmptyPanel />
          </div>
        </div>
      ) : (
        <div className="stats-box stats-box--bar-chart url-types-chart__bar">
          <div className="stats-box__content">
            <StackedHorizontalBarChart data={stackedBarData} />
          </div>
        </div>
      )}
    </div>
  );
};

export default UrlTypesChart;
